import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Loader2, GripVertical, Image as ImageIcon, Star, X } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import ImageUploader, { MultiImageUploader } from '@/components/ImageUploader';
import IconPicker, { getIconComponent } from '@/components/IconPicker';

// Componente per immagine sortabile nella gallery
function SortableImageItem({ image, onSetCover, onRemove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: image.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative aspect-square bg-muted rounded-sm overflow-hidden group ${isDragging ? 'z-50' : ''}`}
    >
      <img
        src={image.thumbnail_url || image.url}
        alt={image.alt_text || 'Immagine servizio'}
        className="w-full h-full object-cover"
      />
      
      {/* Cover badge */}
      {image.is_cover && (
        <div className="absolute top-1 left-1 bg-yellow-500 text-white px-1.5 py-0.5 rounded text-xs font-medium">
          Cover
        </div>
      )}
      
      {/* Overlay con azioni */}
      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
        {/* Drag handle */}
        <button
          {...attributes}
          {...listeners}
          className="p-1.5 bg-white/20 hover:bg-white/40 rounded cursor-grab active:cursor-grabbing"
          title="Trascina per riordinare"
        >
          <GripVertical size={16} className="text-white" />
        </button>
        
        {/* Set as cover */}
        {!image.is_cover && (
          <button
            onClick={onSetCover}
            className="p-1.5 bg-white/20 hover:bg-yellow-500/80 rounded"
            title="Imposta come cover"
          >
            <Star size={16} className="text-white" />
          </button>
        )}
        
        {/* Remove */}
        <button
          onClick={onRemove}
          className="p-1.5 bg-white/20 hover:bg-red-500/80 rounded"
          title="Rimuovi"
        >
          <X size={16} className="text-white" />
        </button>
      </div>
    </div>
  );
}

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    duration: '',
    starting_price: '',
    features: '',
    faq: [],
    icon: '',
    image: null,
    image_thumbnail: null,
    images: [],
    order: 0,
    is_active: true
  });

  // Sensors per drag & drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchServices = useCallback(async () => {
    try {
      const response = await api.get('/admin/services');
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const openDialog = (service = null) => {
    if (service) {
      setEditingService(service);
      const coverImage = service.images?.find(img => img.is_cover) || service.images?.[0];
      setFormData({
        title: service.title,
        description: service.description,
        category: service.category,
        duration: service.duration || '',
        starting_price: service.starting_price || '',
        features: service.features?.join('\n') || '',
        faq: service.faq || [],
        icon: service.icon || '',
        image: service.image || coverImage?.url || null,
        image_thumbnail: service.image_thumbnail || coverImage?.thumbnail_url || null,
        images: service.images || [],
        order: service.order,
        is_active: service.is_active
      });
    } else {
      setEditingService(null);
      setFormData({
        title: '',
        description: '',
        category: '',
        duration: '',
        starting_price: '',
        features: '',
        faq: [],
        icon: '',
        image: null,
        image_thumbnail: null,
        images: [],
        order: services.length,
        is_active: true
      });
    }
    setDialogOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (imageData) => {
    if (imageData) {
      setFormData(prev => ({
        ...prev,
        image: imageData.url,
        image_thumbnail: imageData.thumbnail_url
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        image: null,
        image_thumbnail: null
      }));
    }
  };

  // Handler per gallery immagini multiple
  const handleImagesChange = (newImages) => {
    // Aggiorna l'ordine
    const imagesWithOrder = newImages.map((img, idx) => ({
      ...img,
      order: idx,
      is_cover: idx === 0 ? true : img.is_cover
    }));
    
    // La prima immagine diventa anche la cover legacy
    const coverImage = imagesWithOrder.find(img => img.is_cover) || imagesWithOrder[0];
    
    setFormData(prev => ({
      ...prev,
      images: imagesWithOrder,
      image: coverImage?.url || null,
      image_thumbnail: coverImage?.thumbnail_url || null
    }));
  };

  // Imposta un'immagine come cover
  const setImageAsCover = (imageId) => {
    setFormData(prev => {
      const updatedImages = prev.images.map(img => ({
        ...img,
        is_cover: img.id === imageId
      }));
      const coverImage = updatedImages.find(img => img.is_cover);
      return {
        ...prev,
        images: updatedImages,
        image: coverImage?.url || null,
        image_thumbnail: coverImage?.thumbnail_url || null
      };
    });
  };

  // Rimuovi immagine dalla gallery
  const removeImage = (imageId) => {
    setFormData(prev => {
      const updatedImages = prev.images.filter(img => img.id !== imageId);
      // Se rimossa la cover, imposta la prima come cover
      if (updatedImages.length > 0 && !updatedImages.some(img => img.is_cover)) {
        updatedImages[0].is_cover = true;
      }
      const coverImage = updatedImages.find(img => img.is_cover) || updatedImages[0];
      return {
        ...prev,
        images: updatedImages,
        image: coverImage?.url || null,
        image_thumbnail: coverImage?.thumbnail_url || null
      };
    });
  };

  // Drag & drop per riordinare immagini
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      setFormData(prev => {
        const oldIndex = prev.images.findIndex(img => img.id === active.id);
        const newIndex = prev.images.findIndex(img => img.id === over.id);
        const newImages = arrayMove(prev.images, oldIndex, newIndex).map((img, idx) => ({
          ...img,
          order: idx
        }));
        return { ...prev, images: newImages };
      });
    }
  };

  const addFaq = () => {
    setFormData(prev => ({
      ...prev,
      faq: [...prev.faq, { question: '', answer: '' }]
    }));
  };

  const updateFaq = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      faq: prev.faq.map((item, i) => i === index ? { ...item, [field]: value } : item)
    }));
  };

  const removeFaq = (index) => {
    setFormData(prev => ({
      ...prev,
      faq: prev.faq.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.description) {
      toast.error('Compila titolo e descrizione');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        features: formData.features ? formData.features.split('\n').filter(f => f.trim()) : []
      };

      if (editingService) {
        await api.put(`/admin/services/${editingService.id}`, payload);
        toast.success('Servizio aggiornato');
      } else {
        await api.post('/admin/services', payload);
        toast.success('Servizio creato');
      }

      setDialogOpen(false);
      fetchServices();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo servizio?')) return;

    try {
      await api.delete(`/admin/services/${serviceId}`);
      toast.success('Servizio eliminato');
      fetchServices();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Errore nell\'eliminazione');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Caricamento...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-services-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl mb-2">Servizi</h1>
          <p className="text-muted-foreground">Gestisci i servizi offerti</p>
        </div>
        <Button onClick={() => openDialog()} data-testid="new-service-btn">
          <Plus size={16} className="mr-2" />
          Nuovo Servizio
        </Button>
      </div>

      {/* Services List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((service, index) => (
          <motion.div
            key={service.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="bg-card border border-border overflow-hidden"
            data-testid={`service-item-${service.id}`}
          >
            {/* Service Image */}
            <div className="aspect-video bg-muted relative">
              {service.image ? (
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                  <ImageIcon size={32} />
                </div>
              )}
              {!service.is_active && (
                <span className="absolute top-2 right-2 bg-gray-500 text-white px-2 py-0.5 text-xs">
                  Disattivato
                </span>
              )}
            </div>

            {/* Content */}
            <div className="p-4">
              <h3 className="font-heading text-xl mb-1">{service.title}</h3>
              <p className="text-muted-foreground text-sm mb-3 line-clamp-2">{service.description}</p>
              
              <div className="flex flex-wrap gap-3 text-sm text-muted-foreground mb-4">
                {service.duration && <span>Durata: {service.duration}</span>}
                {service.starting_price && (
                  <span className="font-medium text-foreground">{service.starting_price}</span>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openDialog(service)}
                  data-testid={`edit-service-${service.id}`}
                >
                  <Edit size={14} className="mr-1" />
                  Modifica
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(service.id)}
                  className="text-destructive hover:text-destructive"
                  data-testid={`delete-service-${service.id}`}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {services.length === 0 && (
        <div className="text-center py-16 text-muted-foreground">
          <p className="mb-4">Nessun servizio ancora.</p>
          <Button onClick={() => openDialog()}>Crea il primo servizio</Button>
        </div>
      )}

      {/* Service Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingService ? 'Modifica Servizio' : 'Nuovo Servizio'}</DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="info" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="info">Informazioni</TabsTrigger>
              <TabsTrigger value="gallery">Gallery Immagini</TabsTrigger>
              <TabsTrigger value="details">Dettagli</TabsTrigger>
              <TabsTrigger value="faq">FAQ</TabsTrigger>
            </TabsList>

            {/* Info Tab */}
            <TabsContent value="info" className="space-y-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-2 block">Titolo *</Label>
                    <Input
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="Es: Matrimoni"
                      data-testid="service-title-input"
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block">Icona</Label>
                    <IconPicker
                      value={formData.icon}
                      onChange={(icon) => setFormData(prev => ({ ...prev, icon }))}
                    />
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">Categoria</Label>
                  <Input
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    placeholder="matrimoni, ritratti, eventi..."
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                      data-testid="service-active-switch"
                    />
                    <Label>Servizio attivo</Label>
                  </div>
                </div>

              <div>
                <Label className="mb-2 block">Descrizione *</Label>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Descrivi il servizio in dettaglio..."
                />
              </div>
            </TabsContent>

            {/* Gallery Tab - Upload multiplo immagini */}
            <TabsContent value="gallery" className="space-y-6">
              <div>
                <Label className="mb-2 block">Gallery Immagini Servizio</Label>
                <p className="text-sm text-muted-foreground mb-4">
                  Carica più immagini per mostrare esempi del servizio. La prima immagine (o quella con la stella) sarà usata come cover.
                </p>
                
                {/* Upload multiplo */}
                <MultiImageUploader
                  value={formData.images}
                  onChange={handleImagesChange}
                  label=""
                  maxImages={20}
                  maxSize={20}
                />
              </div>

              {/* Gallery preview con drag & drop */}
              {formData.images.length > 0 && (
                <div>
                  <Label className="mb-2 block">Immagini caricate ({formData.images.length})</Label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Trascina per riordinare • Clicca ⭐ per impostare come cover • Clicca ✕ per rimuovere
                  </p>
                  
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={formData.images.map(img => img.id)}
                      strategy={rectSortingStrategy}
                    >
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                        {formData.images.map((image) => (
                          <SortableImageItem
                            key={image.id}
                            image={image}
                            onSetCover={() => setImageAsCover(image.id)}
                            onRemove={() => removeImage(image.id)}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
              )}
            </TabsContent>

            {/* Details Tab */}
            <TabsContent value="details" className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="mb-2 block">Durata indicativa</Label>
                  <Input
                    name="duration"
                    value={formData.duration}
                    onChange={handleInputChange}
                    placeholder="Es: 8-12 ore"
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Prezzo (a partire da)</Label>
                  <Input
                    name="starting_price"
                    value={formData.starting_price}
                    onChange={handleInputChange}
                    placeholder="Es: € 2.500"
                  />
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Cosa include (una voce per riga)</Label>
                <Textarea
                  name="features"
                  value={formData.features}
                  onChange={handleInputChange}
                  rows={6}
                  placeholder="Reportage completo&#10;Album digitale&#10;Video highlights&#10;Consegna in 4 settimane"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Inserisci ogni voce su una riga separata
                </p>
              </div>
            </TabsContent>

            {/* FAQ Tab */}
            <TabsContent value="faq" className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>FAQ specifiche del servizio</Label>
                <Button type="button" variant="outline" size="sm" onClick={addFaq}>
                  <Plus size={14} className="mr-1" />
                  Aggiungi FAQ
                </Button>
              </div>

              <div className="space-y-4">
                {formData.faq.map((item, index) => (
                  <div key={index} className="border border-border p-4 rounded-sm space-y-3">
                    <Input
                      value={item.question}
                      onChange={(e) => updateFaq(index, 'question', e.target.value)}
                      placeholder="Domanda"
                    />
                    <Textarea
                      value={item.answer}
                      onChange={(e) => updateFaq(index, 'answer', e.target.value)}
                      placeholder="Risposta"
                      rows={2}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFaq(index)}
                      className="text-destructive"
                    >
                      <Trash2 size={14} className="mr-1" />
                      Rimuovi
                    </Button>
                  </div>
                ))}
                {formData.faq.length === 0 && (
                  <p className="text-muted-foreground text-center py-4 text-sm">
                    Nessuna FAQ. Aggiungi domande frequenti specifiche per questo servizio.
                  </p>
                )}
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleSubmit} disabled={saving} data-testid="save-service-btn">
              {saving ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Salvataggio...
                </>
              ) : (
                'Salva Servizio'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
